import { useMemo } from 'react'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import PropTypes from 'prop-types'
import { useShowWithDelay } from '~/hooks'
import Spinner from './Spinner'

const ActionButton = (props) => {
  const {
    background,
    children,
    disabled,
    icon,
    label,
    loading,
    title,
    onClick,
    ...rest
  } = props

  const spinnerVisible = useShowWithDelay(500, loading)

  return useMemo(() => {
    let button
    if (label) {
      button = (
        <Button
          disabled={disabled || loading}
          variant="contained"
          {...rest}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5em',
            ...(icon ? { lineHeight: '24px', padding: 0.5 } : {}),
            ...(rest.sx ?? {}),
          }}
          onClick={onClick}
        >
          {label}
          {loading && spinnerVisible && <Spinner delay={0} width="1em" />}
        </Button>
      )
    } else {
      button = (
        <IconButton
          disabled={disabled || loading}
          {...rest}
          sx={{
            ...(background
              ? {
                  bgcolor: 'primary.main',
                  borderRadius: '4px',
                  padding: 0.5,
                }
              : {
                  color: 'primary.main',
                  padding: 0,
                }),
            ...(rest.size === 'small' ? { padding: 0 } : {}),
            ...(rest.sx ?? {}),
          }}
          variant={background ? 'background' : undefined}
          onClick={onClick}
        >
          {loading && spinnerVisible ? (
            <Spinner delay={0} width="1em" />
          ) : icon ? (
            rest.size === 'small' ? (
              <Box
                as="span"
                sx={{
                  display: 'inline-block',
                  fontSize: 0,
                  transform: 'scale(0.75)',
                }}
              >
                {icon}
              </Box>
            ) : (
              icon
            )
          ) : (
            children
          )}
        </IconButton>
      )
    }
    return title && !disabled && !loading ? (
      <Tooltip title={title}>{button}</Tooltip>
    ) : (
      button
    )
  }, [
    background,
    children,
    disabled,
    icon,
    label,
    loading,
    onClick,
    rest,
    spinnerVisible,
    title,
  ])
}

ActionButton.propTypes = {
  background: function (props, propName, componentName) {
    const value = props[propName]
    if (value && props.label) {
      return new Error(
        `"${propName}" should be used without "label" in "${componentName}"`
      )
    }
    PropTypes.checkPropTypes(
      { [propName]: PropTypes.bool },
      { [propName]: value },
      propName,
      componentName
    )
  },
  disabled: PropTypes.bool,
  icon: function (props, propName, componentName) {
    const value = props[propName]
    if (props.label) {
      PropTypes.checkPropTypes(
        { [propName]: PropTypes.bool },
        { [propName]: value },
        propName,
        componentName
      )
    } else {
      PropTypes.checkPropTypes(
        { [propName]: PropTypes.node },
        { [propName]: value },
        propName,
        componentName
      )
    }
  },
  label: PropTypes.string,
  loading: PropTypes.bool,
  title: PropTypes.string,
  onClick: PropTypes.func,
}

export default ActionButton
